import {
    BarGradientLabel,
    Box,
    InfoIcon,
    Link,
    MarkdownFormatter,
    Typography,
    TypographyProps,
    Tooltip
} from '@methanesat/ui-components';

import { useTranslate } from '../../../../../hooks';
import { getConfidencePercentage } from '../../../../../utils';
import { formatEmissions } from '../../../../../utils/numbers';
import { METHANESAT_FAQ_URL } from '../../../../../consts';

const CITooltip = () => {
    const t = useTranslate();
    const title = t('emissionsMapPage.mapModals.common.confidenceIntervals.tooltipText', { link: METHANESAT_FAQ_URL });
    return (
        <Tooltip
            enterTouchDelay={0}
            leaveTouchDelay={300000}
            title={
                <MarkdownFormatter
                    markdown={title}
                    components={{
                        a({ href, children }) {
                            return (
                                <Link
                                    fontSize="inherit"
                                    href={href}
                                    target="_blank"
                                    underline="always"
                                    data-testid="more-info-link"
                                >
                                    {children}
                                </Link>
                            );
                        }
                    }}
                />
            }
        >
            <InfoIcon
                role="button"
                fontSize="small"
                data-testid="ci-emissions-tooltip"
                sx={{
                    color: 'inherit',
                    padding: '0px',
                    marginLeft: '4px',
                    verticalAlign: 'super',
                    fontSize: '14px'
                }}
            />
        </Tooltip>
    );
};

type CIVariant = 'percent' | 'range';

export const EmissionsHeadline = ({
    'data-testid': dataTestId,
    emissions,
    emissionsText,
    highEmissions,
    lowEmissions,
    units,
    typographyVariant,
    align,
    ciVariant = 'range'
}: {
    'data-testid'?: string;
    emissions: number;
    emissionsText?: string;
    highEmissions?: number | null;
    lowEmissions?: number | null;
    units: string;
    typographyVariant?: TypographyProps['variant'];
    align?: TypographyProps['textAlign'];
    ciVariant?: CIVariant;
}) => {
    const t = useTranslate();
    const formattedEmissions = emissions ? formatEmissions(emissions, units) : null;

    const unitsLabel = units ? ` ${units}` : '';

    const lowAndHighEmissions = lowEmissions && highEmissions;
    const lowOrHighEmissions = lowEmissions ?? highEmissions;

    let confidenceLabel = '';
    let emissionsDisplay = '';

    let showRange = false;

    if (emissionsText) {
        // if we have specific text to display (ex. low emissions), that overrides everything else
        emissionsDisplay = emissionsText;
    } else if (lowAndHighEmissions && ciVariant === 'range') {
        // if both low & high bounds are present and range is selected, display the range
        confidenceLabel = ` ${t('emissionsMapPage.mapModals.emissions.confidence.range', { lowEmissions: formatEmissions(lowEmissions), highEmissions: formatEmissions(highEmissions) })}`;
        emissionsDisplay = `${formattedEmissions} ${unitsLabel} (mean)`;
        showRange = true;
    } else if (lowOrHighEmissions && ciVariant === 'percent') {
        // if either low or high bounds are present and percent is selected (ex. plumes) show the percent only
        const confidencePercentage = getConfidencePercentage(emissions, lowOrHighEmissions);
        confidenceLabel = ` ${t('emissionsMapPage.mapModals.emissions.confidence.percent', { confidencePercentage })}`;
        emissionsDisplay = emissionsText || `${formattedEmissions}${unitsLabel}${confidenceLabel || null}`;
    } else {
        emissionsDisplay = `${formattedEmissions} ${unitsLabel}`;
    }

    return (
        <Box
            width="100%"
            data-testid={dataTestId}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <BarGradientLabel
                    label={emissionsDisplay}
                    typographyVariant={typographyVariant ? typographyVariant : undefined}
                    align={align ? align : undefined}
                />
            </Box>
            {showRange && (
                <Box display="flex" justifyContent="center" alignItems="flex-start" marginTop={1}>
                    <Typography display="inline" textAlign="center" fontSize="0.7rem">
                        {confidenceLabel}
                    </Typography>
                    <CITooltip />
                </Box>
            )}
        </Box>
    );
};
